/**
 * @module builder
 *
 * curl http://localhost:9001/graphql -H "api-key: <your api key>" -H "Content-Type: application/json" -H "Accept: application/json" -X POST -d '{"operationName": "publicCreateQuote", "query": "mutation publicCreateQuote($quote: JSON) { publicCreateQuote(quote: $quote)}", "variables": { "quote": {}}}'
 */

import type {
  OptionsDocument,
  OrderItem,
  Payment,
  QuoteDocument,
  QuoteJobAddress,
  QuoteSource,
  RatesDocument
} from 'paintscout'

import find from 'lodash/find'
import get from 'lodash/get'
import type { Contact, Deal, Owner } from '@paintscout/api'
import moment from 'moment'
import type { QuoteTemplateOption } from '../../../options'
import { generateTaxRateDetails, getQuoteTypeOptions } from '../../../options'

import { uuid } from '../../../util'
import { setContact } from '../../contact'

import { isEmpty } from '../../../../util'

import { parseNumber, roundPrice } from '../../../../calculator'
import { CURRENT_QUOTE_VERSION } from '../../../../constants'
import { getFeatures } from '../../../options/get-features'
import { getInitialPresentation } from '../../presentation/get-initial-presentation'
import { setPresentationTemplate } from '../../quotes/set-presentation-template'
import { getInitialTerms } from '../../terms/get-initial-terms'
import { calculateQuote } from '../../util'
import { sanitizeTemplate } from '../../util/sanitize-template'
import { updateToLatestTerms } from '../transform-legacy-quote/update-to-latest-terms'

export function createQuote(args?: {
  options: OptionsDocument
  rates?: RatesDocument
  quoteType?: string
  owner?: Owner
  signature?: string
  template?: QuoteTemplateOption
  contact?: Partial<Contact>
  deal?: Deal
  invoice?: boolean
  type?: 'quote' | 'quoteTemplate'
  quoteDate?: number
  estimatorNotes?: string
  clientNote?: string
  crewNote?: string
  jobIdentifier?: string
  jobAddress?: QuoteJobAddress
  hourlyRate?: number
  source?: QuoteSource
  sources?: Record<string, QuoteSource>
}): QuoteDocument {
  const {
    options,
    rates,
    template,
    owner,
    hourlyRate: hourlyRateArg,
    contact,
    invoice,
    signature,
    source,
    sources,
    deal
  } = args

  const id = uuid()

  const quoteDate = args?.quoteDate ?? moment().startOf('day').unix() * 1000

  const updated = Date.now()

  const { templateSurchargeOverride } = getFeatures({ options })
  const { smartTemplate: smartTemplateFeature } = getFeatures({ options }).quotes
  const smartTemplate = smartTemplateFeature || template?.smartTemplate || false

  const quoteTypeOptions = getQuoteTypeOptions({ options })
  let quoteType
  if (args.quoteType) {
    quoteType = find(quoteTypeOptions, { key: args.quoteType })
    if (!quoteType) {
      quoteType = quoteTypeOptions[0]
    }
  } else if (template?.content?.int_ext?.value && find(quoteTypeOptions, { key: template.content.int_ext.value })) {
    quoteType = find(quoteTypeOptions, { key: template.content.int_ext.value })
  } else {
    quoteType = quoteTypeOptions[0]
  }

  const globalHourlyRate = get(options.options, 'hourlyRate.value')
  const quoteTypeHourlyRate = get(quoteType, 'hourlyRate')
  const hourlyRate = hourlyRateArg ? hourlyRateArg : quoteTypeHourlyRate ? quoteTypeHourlyRate : globalHourlyRate

  const { taxRate, taxDescription, taxDetails } = generateTaxRateDetails({ options })

  const presentationTemplate = getInitialPresentation({ options, quoteType })

  const quoteOptions = get(options.options, 'quotes')
  if (quoteOptions.serviceFees?.hiddenValue) {
    quoteOptions.showPrices = false
  }

  let estimatorNotes = [template?.content?.estimatorNotes, args?.estimatorNotes].filter((item) => item).join('<p></p>')
  if (!estimatorNotes) {
    estimatorNotes = '<p></p>'
  }

  const addButtonLocations =
    template && template.content?.options ? template.content.options.addIndex : { bid: -1, index: -1 }

  let clientNote
  if (args.clientNote && !isEmpty(args.clientNote)) {
    clientNote = args.clientNote
  } else if (template?.content?.client_note && !isEmpty(template.content.client_note)) {
    clientNote = template.content.client_note
  } else {
    clientNote = '<p></p>'
  }

  let crewNote
  if (args.crewNote && !isEmpty(args.crewNote)) {
    crewNote = args.crewNote
  } else if (template?.content?.internal_note && !isEmpty(template.content.internal_note)) {
    crewNote = template.content.internal_note
  } else {
    crewNote = '<p></p>'
  }

  let jobIdentifier = ''
  if (args?.jobIdentifier) {
    jobIdentifier = args.jobIdentifier
  } else if (template?.content?.job_identifier) {
    jobIdentifier = template.content.job_identifier
  }

  let jobAddress: QuoteJobAddress = {
    address: '',
    city: '',
    province: '',
    postal: ''
  }
  if (args?.jobAddress) {
    jobAddress = args.jobAddress
  } else if (deal?.jobAddress) {
    jobAddress = { ...deal.jobAddress }
  } else if (template?.content?.jobAddress) {
    jobAddress = { ...template.content.jobAddress }
  }

  const signatures: any = {}
  if (signature) {
    signatures.estimator = signature
  }

  const status: QuoteDocument['status'] = {
    value: 'draft' as const,
    updated,
    history: {
      draft: {
        day: moment().startOf('day').unix() * 1000,
        timestamp: updated,
        firstTimestamp: updated
      }
    }
  }

  let areaType
  if (quoteType.defaultAreaType) {
    areaType = quoteType.defaultAreaType
  } else if (quoteType.key === 'ext') {
    areaType = 'surface'
  } else {
    areaType = 'room'
  }

  const intExt = {
    value: quoteType.key,
    label: quoteType.label,
    defaultAreaType: areaType
  }

  const type = args.type ? args.type : 'quote'

  const terms = getInitialTerms({ options, quoteType, invoice })

  const assignedUsers = owner?.id
    ? {
        [owner.id]: {
          assigned: true
        }
      }
    : {}

  const discountOptions = options.options.discounts.order.map((discountKey) => ({
    ...options.options.discounts.values[discountKey],
    key: discountKey
  }))

  const defaultDiscounts = template ? [] : discountOptions.filter((discount) => discount.isDefault)
  const { discount, discounts } = defaultDiscounts.reduce(
    (acc, curr) => {
      const currentItemValue = curr.type === 'amount' ? curr.value : (0 * parseNumber(curr.value, 0)) / 100
      const roundedValue = roundPrice(parseNumber(currentItemValue, 0), options.options.quotes.roundPrices)

      acc.discounts = [
        ...acc.discounts,
        {
          key: curr.key,
          description: curr.description || '',
          label: curr.description,
          price: parseNumber(curr.value, 0),
          value: parseNumber(curr.value, 0),
          type: curr.type,
          visible: true
        }
      ]
      acc.discount.value += roundedValue
      acc.discount.description += acc.discount.description ? `, ${curr.description}` : curr.description

      return acc
    },
    {
      discount: {
        value: 0,
        description: '',
        type: 'amount',
        key: defaultDiscounts?.length ? (defaultDiscounts?.length === 1 ? defaultDiscounts[0].key : 'multi') : ''
      },
      discounts: []
    }
  )

  let baseQuote: QuoteDocument = {
    _id: id,
    type,
    smartTemplate: type === 'quoteTemplate' && smartTemplate,
    version: CURRENT_QUOTE_VERSION,
    name: '',
    hourly_rate: hourlyRate,
    tax_rate: taxRate,
    tax_description: taxDescription,
    tax_details: taxDetails,

    created_by: owner?.id ?? null,
    owner,
    assignedUsers,

    discount: parseNumber(discount?.value, 0),
    discounts,
    discount_key: discount?.key ?? '',
    discount_type: {
      value: discount?.type ?? 'percentage'
    },
    discount_description: discount?.description ?? '',
    discount_isShown: !!discount,

    client_note: clientNote,
    internal_note: crewNote,

    presentationTemplate,
    status,
    int_ext: intExt,

    areas: {},
    lineItems: {},
    groups: {},

    substrates: {},
    substratesGroupBy: 'client_label',

    order: {
      area: [] as OrderItem[],
      substrate: [] as OrderItem[],
      crew: [] as OrderItem[]
    },

    sentReports: {},

    dates: {
      created: updated,
      updated,
      client: {},
      staff: {},
      lastStatusUpdate: updated
    },

    signatures,

    clientVisible: true,
    crewVisible: true,

    totals: {
      hours: 0,
      price: 0,
      prep: 0,
      line_items: {
        price: 0,
        hours: 0,
        prep: 0
      },
      groups: {},
      service_fee_visible: 0,
      service_fee_hidden: 0,
      total_discount: 0,
      after_discount: 0,
      hours_after_discount: 0,
      total_tax: 0,
      after_tax: 0
    },
    followUps: {},
    job_identifier: jobIdentifier,
    jobAddress,

    defaultView: 'area' as const,

    options: {
      ...quoteOptions,
      addIndex: {
        ...addButtonLocations
      },
      ...(Object.keys(template?.content?.options?.surcharge ?? {}).length
        ? {
            surcharge: {
              enabled: false,
              value: 0,
              applyToDeposit: false,
              processDebit: false,
              ...(quoteOptions?.surcharge ?? {}),
              ...(templateSurchargeOverride ? template?.content?.options?.surcharge ?? {} : {})
            }
          }
        : {}),
      showPrices: template?.content?.options?.showPrices ?? quoteOptions.showPrices
    },

    source,
    sources,
    deal_id: deal?._id ?? null,

    terms,

    payments: [] as Payment[],
    quote_date: quoteDate,
    is_invoice: !!invoice
  }

  if (contact) {
    baseQuote = setContact({ quote: baseQuote, contact })
  }

  const newQuote = {
    ...baseQuote,
    ...sanitizeTemplate({ baseQuote, template, smartTemplate: template?.smartTemplate, rates, options }),
    presentationTemplate: setPresentationTemplate({ template, presentationTemplate, options }),
    terms:
      template?.content?.terms && !template.smartTemplate
        ? updateToLatestTerms({ terms: template.content.terms, options })
        : terms,
    int_ext: intExt,
    estimatorNotes
  }

  return calculateQuote({ quote: newQuote, options })
}
